import { STAGE_META, WORKFLOW_STAGES, WorkItem, moneyFigure, powerFigure } from '../../models/campaign-workflow.model';

export type AreaMetric = 'stage' | 'kwp' | 'invest' | 'payback' | 'roi';

export interface AreaBin {
  key: string;
  label: string;
  color: string;
  tip?: string;
  test: (item: WorkItem) => boolean;
}

export const AREA_METRICS: { id: AreaMetric; label: string; title: string }[] = [
  { id: 'kwp', label: 'Leistung', title: 'PV-Leistung je Anlage' },
  { id: 'invest', label: 'Investition', title: 'Investition je Anlage' },
  { id: 'payback', label: 'Amortisation', title: 'Amortisation je Anlage' },
  { id: 'roi', label: 'Rendite', title: 'Rendite je Anlage' },
  { id: 'stage', label: 'Stand', title: 'Stand der Analysen' },
];

/** One blue from light to dark; darker is always the better or bigger value. */
const RAMP = ['#c7d6ff', '#8eafff', '#5e88f5', '#3363ff', '#2447c7', '#172f8a'];
const VALUE: Record<Exclude<AreaMetric, 'stage'>, (item: WorkItem) => number | null> = {
  kwp: (item) => item.planning.kwp,
  invest: (item) => item.planning.invest,
  payback: (item) => item.planning.payback,
  roi: (item) => item.planning.roi,
};
// The size scale is centred on the 16 to 18 kWp most roofs here reach.
const SCALES: Record<Exclude<AreaMetric, 'stage'>, { limits: number[]; labels: string[]; darkFirst?: boolean }> = {
  kwp: { limits: [12, 14, 16, 18, 20], labels: ['unter 12 kWp', '12 bis 14 kWp', '14 bis 16 kWp', '16 bis 18 kWp', '18 bis 20 kWp', 'über 20 kWp'] },
  invest: { limits: [15000, 20000, 25000, 30000, 40000], labels: ['unter 15 T€', '15 bis 20 T€', '20 bis 25 T€', '25 bis 30 T€', '30 bis 40 T€', 'über 40 T€'] },
  payback: { limits: [8, 10, 12, 15, 20], labels: ['unter 8 Jahre', '8 bis 10 Jahre', '10 bis 12 Jahre', '12 bis 15 Jahre', '15 bis 20 Jahre', 'über 20 Jahre'], darkFirst: true },
  roi: { limits: [2, 4, 6, 8, 10], labels: ['unter 2 %', '2 bis 4 %', '4 bis 6 %', '6 bis 8 %', '8 bis 10 %', 'über 10 %'] },
};

/** Stage colours are CSS tokens, but the map draws on a canvas, which needs real values. */
function cssColor(token: string): string {
  const name = token.replace(/^var\((.*)\)$/, '$1');
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || token;
}

export function binsOf(metric: AreaMetric): AreaBin[] {
  if (metric === 'stage') {
    return WORKFLOW_STAGES.map((stage) => ({
      key: stage,
      label: STAGE_META[stage].label,
      color: cssColor(STAGE_META[stage].color),
      tip: STAGE_META[stage].tip,
      test: (item) => item.stage === stage,
    }));
  }
  const { limits, labels, darkFirst } = SCALES[metric];
  const value = VALUE[metric];
  return labels.map((label, index) => ({
    key: label,
    label,
    color: RAMP[darkFirst ? RAMP.length - 1 - index : index],
    test: (item) => {
      const number = value(item);
      if (number === null) return false;
      return (index === 0 || number >= limits[index - 1]) && (index === limits.length || number < limits[index]);
    },
  }));
}

const fmt = (n: number) => n.toLocaleString('de-DE');
const one = (n: number) => n.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const average = (values: number[]) => values.reduce((all, value) => all + value, 0) / (values.length || 1);
const known = (items: WorkItem[], metric: Exclude<AreaMetric, 'stage'>) => items.flatMap((item) => { const value = VALUE[metric](item); return value === null ? [] : [value]; });

/** The big number above the chart. */
export function headline(metric: AreaMetric, items: WorkItem[]): { value: string; unit: string; hint: string } {
  if (metric === 'stage') {
    const ready = items.filter((item) => item.stage === 'done').length;
    return { value: fmt(items.length), unit: 'Analysen', hint: `${fmt(ready)} davon fertig` };
  }
  const values = known(items, metric);
  const from = `aus ${fmt(values.length)} ${values.length === 1 ? 'Planung' : 'Planungen'}`;
  if (!values.length) return { value: '–', unit: '', hint: 'Noch keine Planungen' };
  const sum = values.reduce((all, value) => all + value, 0);
  if (metric === 'kwp') return { ...powerFigure(sum), hint: from };
  if (metric === 'invest') return { ...moneyFigure(sum), hint: from };
  return metric === 'payback'
    ? { value: one(average(values)), unit: 'Jahre im Schnitt', hint: from }
    : { value: one(average(values)), unit: '% im Schnitt', hint: from };
}

export function streetOf(title: string | null): string | null {
  if (!title) return null;
  return title.split(',')[0].replace(/\s+\d+\s*[a-zA-Z]?(?:\s*[-/]\s*\d+\s*[a-zA-Z]?)?$/, '').trim() || null;
}

/** Where the most power can be sold, street by street. */
export function topStreets(items: WorkItem[], size = 5): { street: string; kwp: number; plants: number; width: number }[] {
  const streets = new Map<string, { kwp: number; plants: number }>();
  for (const item of items) {
    const street = streetOf(item.title);
    if (!street || item.planning.kwp === null) continue;
    const row = streets.get(street) ?? { kwp: 0, plants: 0 };
    row.kwp += item.planning.kwp;
    row.plants++;
    streets.set(street, row);
  }
  const sorted = [...streets].sort((a, b) => b[1].kwp - a[1].kwp).slice(0, size);
  const peak = sorted[0]?.[1].kwp || 1;
  return sorted.map(([street, row]) => ({ street, ...row, width: Math.max(2, (row.kwp / peak) * 100) }));
}

const ECONOMIC_YEARS = 12;
/** Below this the extrapolation says more about chance than about the area. */
const MIN_PLANS_FOR_PROJECTION = 10;

/** What the area is worth, in the terms someone selling PV there thinks in. */
export function areaInsights(items: WorkItem[]): { label: string; value: string; unit: string; hint: string; tip: string }[] {
  const planned = items.filter((item) => item.planning.kwp !== null);
  const blocked = items.filter((item) => item.stage === 'not_plannable');
  const considered = planned.length + blocked.length;
  const hasSolar = blocked.filter((item) => item.reason === 'existing_solar').length;
  const tooSmall = blocked.filter((item) => item.reason === 'roof_too_small').length;
  const share = considered ? planned.length / considered : 0;
  const paybacks = known(items, 'payback');
  const economic = paybacks.filter((years) => years < ECONOMIC_YEARS).length;
  const rois = known(items, 'roi');
  const kwps = known(items, 'kwp');
  const invests = known(items, 'invest');
  const modules = items.flatMap((item) => (item.planning.modules === null ? [] : [item.planning.modules]));
  const canProject = planned.length >= MIN_PLANS_FOR_PROJECTION && kwps.length > 0;
  const potential = canProject ? items.length * share * average(kwps) : 0;
  const volume = canProject && invests.length ? items.length * share * average(invests) : null;
  return [
    {
      label: 'Planbare Dächer',
      value: considered ? `${Math.round(share * 100)}` : '–',
      unit: considered ? '%' : '',
      hint: `${fmt(hasSolar)} haben schon PV · ${fmt(tooSmall)} zu klein`,
      tip: `${fmt(planned.length)} von ${fmt(considered)} gerechneten Adressen haben eine Planung. Der Rest ist nicht planbar.`,
    },
    {
      label: 'Wirtschaftlich',
      value: paybacks.length ? `${Math.round((economic / paybacks.length) * 100)}` : '–',
      unit: paybacks.length ? '%' : '',
      hint: rois.length ? `unter ${ECONOMIC_YEARS} Jahren · Ø Rendite ${one(average(rois))} %` : `unter ${ECONOMIC_YEARS} Jahren Amortisation`,
      tip: `${fmt(economic)} von ${fmt(paybacks.length)} Anlagen amortisieren sich in unter ${ECONOMIC_YEARS} Jahren.`,
    },
    {
      label: 'Typische Anlage',
      value: kwps.length ? one(average(kwps)) : '–',
      unit: kwps.length ? 'kWp' : '',
      hint: [invests.length ? `Ø ${fmt(Math.round(average(invests)))} €` : '', modules.length ? `${fmt(Math.round(average(modules)))} Module` : ''].filter(Boolean).join(' · ') || 'Noch keine Planungen',
      tip: 'Durchschnitt aller vorliegenden Planungen im Gebiet.',
    },
    {
      label: 'Potenzial im Gebiet',
      ...(canProject ? powerFigure(potential) : { value: '–', unit: '' }),
      hint: canProject ? (volume !== null ? `Hochrechnung · ${moneyFigure(volume).value} ${moneyFigure(volume).unit}` : 'Hochrechnung') : `Ab ${MIN_PLANS_FOR_PROJECTION} Planungen`,
      tip: canProject ? `Hochgerechnet auf alle ${fmt(items.length)} Adressen, mit dem bisherigen Anteil planbarer Dächer und der durchschnittlichen Anlage.` : 'Für eine Hochrechnung braucht es mehr Planungen.',
    },
  ];
}
